<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-row no-gutters>
        <v-col cols="12">
          <div id="map" class="map"></div>
        </v-col>
      </v-row>
      <v-row no-gutters class="pt-2">
        <v-col cols="auto">
          <base-button
            @click="downloadWcsHandler"
            translation-path="button.download"
            :disabled="isDownloadDisabled"
            :loading="loading"
          ></base-button>
          <base-button
            v-if="!loading"
            translation-path="button.deleteBbox"
            @click="reset"
            :disabled="isDownloadDisabled"
          ></base-button>
          <base-button v-else translation-path="button.cancel" @click="cancelWcsDownloadingHandler"></base-button>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { call, get } from 'vuex-pathify';

import { default as Map } from '../plugins/new_map';
import { XYZ } from 'ol/source';
import { Tile as TileLayer } from 'ol/layer';
import { createBox } from 'ol/interaction/Draw';
import { FullScreen, defaults as defaultControls } from 'ol/control';

import view from '@/mixins/map/view';
import basicUtils from '@/mixins/map/utils';
import installCrs from '@/mixins/map/crs';
import identification from '@/mixins/map/identification';
import sidebarGeometryInput from '@/mixins/map/drawModifyInteraction';

export default {
  name: 'NmtSettingsMap',
  mixins: [view, installCrs, sidebarGeometryInput, identification, basicUtils],
  data: () => ({
    geometry: undefined,
    loading: false,
  }),
  computed: {
    basemapLayer: get('layers/basemapLayer'),
    isDownloadDisabled() {
      return this.geometry === undefined || this.loading;
    },
  },
  methods: {
    downloadWcs: call('nmt/downloadWcs'),
    getWcsDownloadStatus: call('nmt/getWcsDownloadStatus'),
    cancelWcsDownloading: call('nmt/cancelWcsDownloading'),
    initMap() {
      const {
        url,
        parameters: { minZoom, maxZoom },
      } = this.basemapLayer;
      const fullscreenControl = new FullScreen();
      this.map = new Map({
        controls: defaultControls().extend([fullscreenControl]),
        layers: [
          new TileLayer({
            source: new XYZ({
              url,
              ...(minZoom || minZoom === 0 ? { minZoom } : {}),
              ...(maxZoom || maxZoom === 0 ? { maxZoom } : {}),
            }),
            name: 'basemapLayer',
          }),
        ],
        target: 'map',
        view: this.prepareView(),
      });
      fullscreenControl.on('leavefullscreen', () => {
        if (this.geometry) {
          const bb = this.geometry.bbox;
          this.map.getView().fit([bb.min_x, bb.min_y, bb.max_x, bb.max_y]);
        }
      });
    },
    onDrawend(e) {
      const [min_x, min_y, max_x, max_y] = e.feature.getGeometry().getExtent();
      this.geometry = {
        bbox: { min_x, min_y, max_x, max_y },
        bbox_srid: this.$_config.defaultEpsg.split(':')[1],
        cell_size_meters: 1,
      };
    },
    initDrawing() {
      this.initDrawingSidebarGeometry({
        drawendCallback: this.onDrawend,
        geometryFunction: createBox(),
        isInitSnapping: false,
        drawType: 'Circle',
      });
    },
    async cancelWcsDownloadingHandler() {
      try {
        clearTimeout(this.timeout);
        await this.cancelWcsDownloading();
        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },
    async checkStatus() {
      try {
        return new Promise(resolve => {
          this.timeout = setTimeout(async () => {
            const r = await this.getWcsDownloadStatus();
            if (r.data.status !== 'done') {
              const r2 = await this.checkStatus();
              resolve(r2);
            } else {
              resolve(r);
            }
          }, 2500);
        });
      } catch (error) {
        console.log('~ error', error);
      }
    },
    async downloadWcsHandler() {
      try {
        this.loading = true;
        await this.downloadWcs(this.geometry);
        await this.checkStatus();
        this.loading = false;
        this.$emit('fetched');
      } catch (error) {
        console.log(error);
      }
    },
    reset() {
      this.geometry = undefined;
      this.deleteSidebarGeometry();
      this.initDrawing();
    },
    beforeDestroy() {
      this.map.setTarget(null);
      this.map = null;
      this.$off();
    },
  },
  mounted() {
    this.initMap();
    this.initDrawing();
    this.$once('hook:beforeDestroy', this.beforeDestroy);
  },
};
</script>

<style lang="scss" scoped>
.map {
  width: 100%;
  height: 25vh;
  @include border(all, 1px, #898989);
  border-radius: 5px;
}
::v-deep .ol-viewport {
  border-radius: inherit;
}
</style>
